import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, RefreshCw, Edit, Trash2, X } from 'lucide-react';
import ohipHotelGroupsService from '../services/OhipHotelGroupsService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import './OhipTransactionsPage.css';
import './OhipHotelGroupsPage.css';

// Admin > Billing > OHIP Environments.
//
// The route is still /admin/billing/hotel-groups: a row IS a hotel group — one x-hotelids query against
// the portal — but the environment it reports on is what people come here to find, so that is the name
// on the screen. Renaming the path would break every bookmark for the sake of a word.
//
// One row = one Analytics query against the Oracle portal: the codes belonging to it go into the
// x-hotelids filter together, so the report's totals are that group's usage. Editing this list changes
// what the daily scrape collects, and therefore what is billed — which is why it lives in the database
// and on this screen rather than as a constant in a lambda.

// dormant defaults to 'Y' — the same default as the column. A group nobody has explicitly cleared is one
// nobody has confirmed should be talking to OHIP, and the safe answer is the one that gets noticed.
// dormant defaults to 'N': a row somebody types into this screen is by definition one they know about.
// 'Y' is set only by ohip-transactions-discover, and means "Oracle started reporting this and nobody has
// looked at it yet".
const EMPTY_FORM = { groupKey: '', name: '', dormant: 'N', notes: '', codesText: '' };

const parseCodes = (text) => [...new Set(
    String(text || '')
        .split(/[\s,;\n]+/)
        .map(c => c.trim().toUpperCase())
        .filter(Boolean)
)];

function OhipHotelGroupsPage() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [dormantOnly, setDormantOnly] = useState(false);

    const [editing, setEditing] = useState(null);   // the row being edited, or 'new'
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (search) params.search = search;
            if (dormantOnly) params.dormant = 'true';
            const res = await ohipHotelGroupsService.list(params);
            setGroups(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to load environments');
            setGroups([]);
        } finally {
            setLoading(false);
        }
    }, [search, dormantOnly]);

    useEffect(() => { load(); }, [load]);

    const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); };
    const openEdit = (g) => {
        setForm({
            groupKey: g.groupKey,
            name: g.name,
            dormant: g.dormant === 'Y' ? 'Y' : 'N',
            notes: g.notes || '',
            codesText: (g.codes || []).join(', '),
        });
        setEditing(g);
    };

    const save = async () => {
        const codes = parseCodes(form.codesText);
        if (!form.name.trim()) { setError('Name is required'); return; }
        if (!codes.length) { setError('At least one hotel code is required'); return; }

        setSaving(true);
        setError(null);
        try {
            if (editing === 'new') {
                if (!form.groupKey.trim()) { setError('Environment key is required'); setSaving(false); return; }
                await ohipHotelGroupsService.create({
                    groupKey: form.groupKey.trim(),
                    name: form.name.trim(),
                    dormant: form.dormant,
                    notes: form.notes,
                    codes,
                });
            } else {
                // groupKey deliberately not sent — it is immutable and the endpoint rejects it.
                await ohipHotelGroupsService.update(editing.id, {
                    name: form.name.trim(),
                    dormant: form.dormant,
                    notes: form.notes,
                    codes,
                });
            }
            setEditing(null);
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        const g = confirmDelete;
        setConfirmDelete(null);
        try {
            await ohipHotelGroupsService.remove(g.id);
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Delete failed');
        }
    };

    const dormantCount = groups.filter(g => g.dormant === 'Y').length;
    const codeCount = new Set(groups.flatMap(g => g.codes || [])).size;

    return (
        <div className="otx-page">
            <div className="otx-header">
                <div>
                    <h1>OHIP Environments</h1>
                    <p className="otx-subtitle">
                        The Oracle environments billing is measured across, and the group of hotel codes
                        reported on together in each. The daily scrape covers every one of them.
                    </p>
                </div>
                <div className="otx-header-actions">
                    <Button variant="secondary" onClick={openNew}><Plus className="icon" /> New environment</Button>
                    <Button variant="success" onClick={load}><RefreshCw className="icon" /> Refresh</Button>
                </div>
            </div>

            <div className="otx-filters">
                <label>
                    <span>Search</span>
                    <input
                        type="text"
                        placeholder="Name, key, hotel code or environment"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ minWidth: 280 }}
                    />
                </label>
                <label className="otx-check">
                    <input type="checkbox" checked={dormantOnly} onChange={e => setDormantOnly(e.target.checked)} />
                    <span>Unacknowledged only</span>
                </label>
                <Button variant="secondary" onClick={load}><Search className="icon" /> Search</Button>
            </div>

            {error && <div className="otx-error"><X size={16} /> {error}</div>}

            <div className="otx-stats">
                <div className="otx-stat">
                    <span className="otx-stat-label">Groups</span>
                    <span className="otx-stat-value">{groups.length}</span>
                </div>
                <div className="otx-stat">
                    <span className="otx-stat-label">Unacknowledged</span>
                    <span className={`otx-stat-value${dormantCount ? ' exr-warn-text' : ''}`}>{dormantCount}</span>
                </div>
                <div className="otx-stat">
                    <span className="otx-stat-label">Distinct hotel codes</span>
                    <span className="otx-stat-value">{codeCount}</span>
                </div>
            </div>

            <div className="otx-table-wrap">
                <table className="otx-table">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Name</th>
                            <th>Environment</th>
                            <th>Hotel codes</th>
                            <th style={{ width: 150 }}>Status</th>
                            <th style={{ width: 110 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6}><Spinner /></td></tr>}
                        {!loading && !groups.length && (
                            <tr><td colSpan={6} className="otx-empty">No environments match.</td></tr>
                        )}
                        {!loading && groups.map(g => (
                            <tr key={g.id} className={g.dormant === 'Y' ? 'ohg-dormant' : ''}>
                                <td><code className="ohg-key">{g.groupKey}</code></td>
                                <td>{g.name}</td>
                                <td>
                                    {/* Usually one environment; a customer with properties in two
                                        tenancies shows both rather than the first one silently winning. */}
                                    {/* THE PORTAL'S NAME, not the id. "mtce18pr" is what Oracle keys an
                                        environment by, but "11 Cadogan Gardens (mtce18pr) CADO" is what
                                        it is CALLED — and the id is inside the name anyway, so showing
                                        the id alone loses the only human-readable part. The names are
                                        joined by ' | ' in the SQL because a group can span tenancies. */}
                                    <div className="ohg-envs" title={g.tenancyIds || ''}>
                                        {(g.environmentNames || '').split(' | ').filter(Boolean).map(e => (
                                            <span key={e} className="ohg-env">{e}</span>
                                        ))}
                                        {!g.environmentNames && <span className="ohg-env-none">—</span>}
                                    </div>
                                </td>
                                <td>
                                    <div className="ohg-codes">
                                        {(g.codes || []).map(c => <span key={c} className="ohg-code">{c}</span>)}
                                    </div>
                                </td>
                                <td>
                                    {/* UNACKNOWLEDGED is the loud state. It means discovery found this
                                        environment on the Developer Portal and nobody has confirmed it
                                        should be live — so its traffic raises an alert. Clearing it is
                                        the acknowledgement. */}
                                    <span className={`ohg-badge ${g.dormant === 'Y' ? 'ohg-badge-dormant' : 'ohg-badge-active'}`}
                                          title={g.dormant === 'Y'
                                              ? 'Discovered but not acknowledged — traffic from it raises an alert'
                                              : 'Known and expected to be sending'}>
                                        {g.dormant === 'Y' ? 'Unacknowledged' : 'Known'}
                                    </span>
                                </td>
                                <td>
                                    <div className="ohg-row-actions">
                                        <button className="ohg-icon-btn" title="Edit" onClick={() => openEdit(g)}>
                                            <Edit size={15} />
                                        </button>
                                        {/* Destructive, superadmin-only screen — marked per the style guide. */}
                                        <button className="ohg-icon-btn ohg-icon-btn-danger sa-only" title="Delete" onClick={() => setConfirmDelete(g)}>
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="pp-modal-backdrop" onClick={() => !saving && setEditing(null)}>
                    <div className="pp-modal ohg-modal" onClick={e => e.stopPropagation()}>
                        <div className="pp-modal-header">
                            <span>{editing === 'new' ? 'New environment' : `Edit ${editing.groupKey}`}</span>
                            <button className="pp-modal-close" onClick={() => setEditing(null)} disabled={saving}>×</button>
                        </div>
                        <div className="pp-modal-body ohg-form">
                            <label>
                                <span>Environment key</span>
                                <input
                                    type="text"
                                    value={form.groupKey}
                                    disabled={editing !== 'new'}
                                    onChange={e => setForm(f => ({ ...f, groupKey: e.target.value }))}
                                    placeholder="g81-NEWHOTEL"
                                />
                                <small>
                                    {editing === 'new'
                                        ? 'Permanent. Used in every stored report and billing record, so it cannot be changed later.'
                                        : 'Cannot be changed — it links this group to its billing history.'}
                                </small>
                            </label>
                            <label>
                                <span>Name</span>
                                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                                <small>The display label. Safe to change at any time.</small>
                            </label>
                            <label>
                                <span>Hotel codes</span>
                                <textarea
                                    rows={4}
                                    value={form.codesText}
                                    onChange={e => setForm(f => ({ ...f, codesText: e.target.value }))}
                                    placeholder="LLR, MIR"
                                />
                                <small>
                                    Separated by commas, spaces or new lines. {parseCodes(form.codesText).length} code(s).
                                    These are reported on together as one total.
                                </small>
                            </label>
                            <label>
                                <span>Notes</span>
                                <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                            </label>
                            <label>
                                <span>Status</span>
                                {/* The style guide's toggle, not a checkbox: this is an on/off state on a
                                    record, which is exactly what that pattern is for. Green = 'N' (live and
                                    expected to send); grey = 'Y' (dormant). */}
                                <button
                                    type="button"
                                    className={`pp-toggle-btn ${form.dormant === 'N' ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                                    onClick={() => setForm(f => ({ ...f, dormant: f.dormant === 'N' ? 'Y' : 'N' }))}
                                    title={form.dormant === 'N' ? 'Click to mark unacknowledged' : 'Click to acknowledge'}
                                >
                                    <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                                    <span className="pp-toggle-label">
                                        {form.dormant === 'N' ? 'Known — expected to be sending' : 'Unacknowledged — traffic alerts'}
                                    </span>
                                </button>
                                <small>
                                    Set automatically when discovery finds an environment Oracle has started
                                    reporting. While it is unacknowledged, any traffic from it raises an alert —
                                    clearing it here is how you say the property is supposed to be live.
                                </small>
                            </label>
                        </div>
                        <div className="ohg-modal-footer">
                            <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
                            <Button variant="success" onClick={save} disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <ConfirmModal
                    title="Delete environment"
                    message={`Delete "${confirmDelete.groupKey}"? Its billing history stays in the index but will no longer have an environment to explain it. Deactivating is usually the better choice.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={doDelete}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}
        </div>
    );
}

export default OhipHotelGroupsPage;
